// Nouns — gender, articles, plurals, diminutives, plus a starter vocabulary
// table with guessed gender. Ported from the legacy views.nouns; VOCABULARY is
// imported directly (old shape: { es, en, cat }).
import { VOCABULARY } from "../data/index.js";
import { renderGuideSections } from "../ui/guideSections.js";

const FEM_OVERRIDES = new Set([
  "mano", "radio", "foto", "moto", "auto", "notte", "arte", "gente", "fame", "nave",
]);
const MASC_OVERRIDES = new Set([
  "problema", "tema", "sistema", "clima", "programma", "poeta", "cane", "paese", "mese", "nome", "pane", "fiore", "mare",
]);

function guessGender(w) {
  if (FEM_OVERRIDES.has(w)) return { g: "f", note: "(la)" };
  if (MASC_OVERRIDES.has(w)) return { g: "m", note: "(il — exception)" };
  if (
    w.endsWith("a") || w.endsWith("zione") || w.endsWith("sione") ||
    w.endsWith("tà") || w.endsWith("tù") || w.endsWith("ione")
  )
    return { g: "f", note: "(la)" };
  if (
    w.endsWith("o") || w.endsWith("ore") || w.endsWith("ame") ||
    w.endsWith("mento")
  )
    return { g: "m", note: "(il)" };
  return { g: "?", note: "(check)" };
}

export function nounsView(app) {
  const nouns = VOCABULARY.filter((v) => v.cat === "noun");

  const vocabTable = `
    <div class="bg-white rounded-2xl border border-sage-100 p-5 mt-6">
      <h3 class="text-lg font-bold text-sage-800 mb-3">Starter Noun Vocabulary</h3>
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
              <th class="py-2 pr-3">Italian</th>
              <th class="py-2 px-3">Gender</th>
              <th class="py-2 px-3">English</th>
            </tr>
          </thead>
          <tbody>
            ${nouns
              .map((n) => {
                const { g, note } = guessGender(n.es);
                const color = g === "f" ? "text-pink-600" : g === "m" ? "text-blue-600" : "text-sage-500";
                return `<tr class="border-b border-sage-50 last:border-0">
                <td class="py-2 pr-3 font-medium text-sage-800">${n.es}</td>
                <td class="py-2 px-3 ${color} font-semibold">${note}</td>
                <td class="py-2 px-3 text-sage-700">${n.en}</td>
              </tr>`;
              })
              .join("")}
          </tbody>
        </table>
      </div>
    </div>
  `;

  renderGuideSections(
    app,
    "Nouns",
    "Italian nouns have grammatical gender (masculine or feminine) and number (singular or plural). The gender of a noun controls the form of articles and adjectives that go with it.",
    [
      {
        title: "1. Gender — Masculine vs Feminine",
        body: `
          <p class="text-sage-700 mb-3">Most nouns ending in <b>-o</b> are masculine; most ending in <b>-a</b> are feminine. Nouns ending in <b>-e</b> can be either, so learn them with their article. There are reliable extra clues and a small set of exceptions.</p>
          <ul class="text-sage-700 text-sm space-y-1 mb-3 list-disc pl-5">
            <li><b>Masculine endings:</b> -o, -ore, -ame, plus most ending in a consonant (loanwords). <i>il libro, il colore, il fiume, il bar.</i></li>
            <li><b>Feminine endings:</b> -a, -zione, -sione, -tà, -tù, -ione. <i>la casa, la stazione, la città, la virtù, la regione.</i></li>
            <li><b>Common exceptions:</b> <i>il problema, il tema, il sistema, il programma, il poeta</i> (masculine despite ending in -a); <i>la mano, la foto, la moto, la radio</i> (feminine despite ending in -o).</li>
            <li><b>Same word, both genders:</b> <i>il/la cantante, il/la turista, il/la nipote</i> — gender depends on the person.</li>
          </ul>
        `,
      },
      {
        title: "2. Articles — il / lo / la / un / una",
        body: `
          <p class="text-sage-700 mb-3">Articles always agree with the noun in gender and number — and their shape also depends on the sound that follows.</p>
          <div class="overflow-x-auto"><table class="w-full text-sm mb-3">
            <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
              <th class="py-2 pr-3"></th><th class="py-2 px-3">Masculine</th><th class="py-2 px-3">Feminine</th>
            </tr></thead>
            <tbody>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">Definite singular</td><td class="py-2 px-3"><b>il</b> libro · <b>lo</b> studente · <b>l'</b>amico</td><td class="py-2 px-3"><b>la</b> casa · <b>l'</b>amica</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">Definite plural</td><td class="py-2 px-3"><b>i</b> libri · <b>gli</b> studenti</td><td class="py-2 px-3"><b>le</b> case</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">Indefinite singular</td><td class="py-2 px-3"><b>un</b> libro · <b>uno</b> studente</td><td class="py-2 px-3"><b>una</b> casa · <b>un'</b>amica</td></tr>
              <tr><td class="py-2 pr-3 font-medium">Indefinite plural (some)</td><td class="py-2 px-3"><b>dei</b> libri · <b>degli</b> studenti</td><td class="py-2 px-3"><b>delle</b> case</td></tr>
            </tbody>
          </table></div>
          <p class="text-sage-700 text-sm"><b>Heads-up:</b> use <b>lo</b>/<b>gli</b>/<b>uno</b> before <b>s + consonant</b>, <b>z</b>, <b>gn</b>, <b>ps</b>, <b>x</b>, <b>y</b> — <i>lo studente, gli zaini, uno psicologo</i> — and <b>l'</b>/<b>un'</b> before a vowel: <i>l'amico, un'amica</i>.</p>
        `,
      },
      {
        title: "3. Plurals",
        body: `
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li>Masculine <b>-o → -i</b>: <i>libro → libri, gatto → gatti</i>.</li>
            <li>Feminine <b>-a → -e</b>: <i>casa → case, ragazza → ragazze</i>.</li>
            <li>Either gender <b>-e → -i</b>: <i>cane → cani, chiave → chiavi</i>.</li>
            <li>Keep the hard sound: <b>-ca/-ga → -che/-ghe</b> (<i>amica → amiche, riga → righe</i>); <b>-co/-go</b> vary (<i>amico → amici</i> but <i>fuoco → fuochi</i>).</li>
            <li><b>Invariable:</b> words ending in an accented vowel or a consonant don't change — <i>la città → le città, il film → i film, la foto → le foto</i>.</li>
          </ul>
        `,
      },
      {
        title: "4. Diminutives & Augmentatives",
        body: `
          <p class="text-sage-700 mb-2">Italian loves modifying nouns with suffixes for size, affection, or emphasis.</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>-ino / -ina</b> (small, cute): <i>gatto → gattino, casa → casina</i>.</li>
            <li><b>-etto / -etta</b> (small): <i>libro → libretto, casa → casetta</i>.</li>
            <li><b>-one</b> (big): <i>libro → librone</i>; note it can flip gender: <i>la porta → il portone</i> (big door).</li>
            <li><b>-accio / -accia</b> (bad, nasty): <i>tempo → tempaccio</i> (awful weather), <i>parola → parolaccia</i> (swear word).</li>
          </ul>
        `,
      },
    ],
    "",
    vocabTable
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
